import { ConfigProvider as AntdConfigProvider, App, ThemeConfig } from "antd";
import { ReactNode, useMemo } from "react";

export const ConfigProvider = ({ children }: { children?: ReactNode }) => {
  const themeConfig = useMemo<ThemeConfig>(() => {
    const computedStyle = getComputedStyle(document.documentElement);
    const getValue = (name: `--${string}`) =>
      computedStyle.getPropertyValue(name).trim();
    return {
      token: {
        colorText: getValue("--text-color-on-primary"),
        colorPrimary: getValue("--primary-button-color-button-primary"),
        colorPrimaryActive: getValue("--primary-button-color-primary-pressed"),
        colorPrimaryHover: getValue("--primary-button-color-primary-hover"),
        colorSuccess: getValue("--success-color-success"),
        colorSuccessActive: getValue("--success-color-success-pressed"),
        colorSuccessHover: getValue("--success-color-success-hover"),
        colorError: getValue("--error-warning-color-error"),
        colorErrorBorder: getValue("--error-warning-color-error"),
        colorErrorActive: getValue("--error-warning-color-error-pressed"),
        colorErrorHover: getValue("--error-warning-color-error-hover"),
        colorErrorBorderHover: getValue("--error-warning-color-error-hover"),
        colorWarning: getValue("--error-warning-color-warning"),
        colorWarningActive: getValue("--error-warning-color-warning-pressed"),
        colorWarningHover: getValue("--error-warning-color-warning-hover"),
        colorBgElevated: getValue("--background-surface-color-on-background"),
        colorIcon: getValue("--pure-white"),
        colorBgContainerDisabled: getValue(
          "--primary-button-color-primary-disabled",
        ),
        colorTextDisabled: getValue("--text-color-on-surface"),
        borderRadius: 8,
        colorTextPlaceholder: getValue("--text-color-on-surface"),
        controlOutline: "none",
      },
      components: {
        Tooltip: {
          fontSize: 14,
          margin: 0,
        },
        Input: {
          colorBorder: "var(--background-surface-color-outline)",
        },
        Menu: {
          itemBorderRadius: 12,
          itemBg: getValue("--background-surface-color-on-background"),
          itemHoverBg: getValue("--background-surface-color-outline"),
          groupTitleColor: getValue("--text-color-on-primary"),
          darkGroupTitleColor: getValue("--text-color-on-primary"),
          itemSelectedColor: getValue("--text-color-on-primary"),
          itemActiveBg: getValue("--background-surface-color-outline"),
          itemSelectedBg: getValue("--background-surface-color-outline"),
          itemMarginBlock: 0,
          itemMarginInline: 0,
          zIndexPopup: 100,
          fontSize: 16,
        },
        Tag: {
          borderRadiusSM: 50,
          colorSuccess: "var(--primary-accent-color-success)",
          colorSuccessBg: "var(--primary-accent-color-success-bg-opacity-10)",
          colorInfo: "var(--text-color-on-primary)",
          colorInfoBg: "#ffffff1A",
          colorWarning: "var(--primary-accent-color-warning)",
          colorWarningBg: "var(--primary-accent-color-warning-opacity-10)",
          colorError: "var(--primary-accent-color-error)",
          colorErrorBg: "var(--primary-accent-color-error-bg-opacity-10)",
        },
        Typography: {
          colorText: "var(--text-color-on-primary)",
          colorLink: "var(--primary-accent-color-primary)",
          linkFontSize: 14,
        },
        Form: {
          labelColor: getValue("--text-color-on-surface"),
          verticalLabelPadding: 4,
        },
        Table: {
          headerBg: "var(--background-surface-color-background)",
          bodySortBg: "unset",
          rowSelectedBg: "var(--background-surface-color-on-background)",
          rowSelectedHoverBg: "var(--background-surface-color-on-background)",
          fixedHeaderSortActiveBg:
            "var(--background-surface-color-on-background)",
          colorBgContainer: "var(--background-surface-color-background)",
          rowHoverBg: "var(--background-surface-color-on-background)",
          headerSortHoverBg: "var(--background-surface-color-on-background)",
          headerSortActiveBg: "var(--background-surface-color-background)",
          headerSplitColor: "transparent",
          headerColor: "var(--text-color-on-primary)",
          cellPaddingInlineSM: 8,
          cellPaddingInlineMD: 12,
          cellPaddingBlockMD: 12,
          colorText: "var(--text-color-on-primary)",
          fontSize: 16,
        },
        Button: {
          controlHeightSM: 32,
          controlHeight: 40,
          controlHeightLG: 48,
          fontWeight: 500,
          // Text color of filled (primary) danger buttons, which sit on the error-red fill
          dangerColor: getValue("--pure-white"),
          defaultBg: "transparent",
          defaultHoverBg: "transparent",
          defaultActiveBg: "transparent",
          defaultColor: "var(--text-color-on-primary)",
          defaultHoverBorderColor: "inherit",
          defaultHoverColor: getValue("--primary-button-color-primary-hover"),
          borderColorDisabled: getValue(
            "--primary-button-color-primary-disabled",
          ),
          paddingContentHorizontal: 16,
          paddingContentHorizontalLG: 16,
          paddingInline: 16,
          fontSizeLG: 16,
          fontSizeSM: 12,
          colorLink: "var(--text-color-on-primary)",
          colorLinkHover: "var(--primary-accent-color-primary)",
          colorLinkActive: "var(--primary-accent-color-primary)",
        },
        Checkbox: {
          colorPrimary: getValue("--primary-accent-color-primary"),
          colorBgContainer: "transparent",
          colorBorder: getValue("--background-surface-color-outline"),
          colorError: getValue("--error-warning-color-error"),
          borderRadiusSM: 2,
          controlInteractiveSize: 16,
          checkboxSize: 16,
          checkboxCheckColor: getValue("--primary-accent-color-primary"),
          checkboxBorderRadius: 2,
          controlOutline: getValue("--primary-accent-color-primary"),
        },
      },
    };
  }, []);
  return (
    <AntdConfigProvider theme={themeConfig}>
      <App>{children}</App>
    </AntdConfigProvider>
  );
};
